import { useEffect, useState } from "react";
import type {
  ArrayPath,
  Control,
  FieldArrayWithId,
  FieldValues,
  UseFieldArrayRemove,
  UseFieldArrayUpdate,
} from "react-hook-form";
import { useFieldArray, useFormContext, useWatch } from "react-hook-form";
import { twJoin } from "tailwind-merge";

import type { QuestionDto } from "@/api/types";
import { IconButton } from "@/components/Button/IconButton";
import { DynamicRepeater } from "@/components/DynamicRepeater/DynamicRepeater";
import { Form } from "@/components/Form/Form";
import { FormCheckbox } from "@/components/Form/FormCheckbox";
import { FormErrorWrapper } from "@/components/Form/FormErrorWrapper";
import { FormInput } from "@/components/Form/FormInput";
import { FormSelect } from "@/components/Form/FormSelect";
import { FormTextArea } from "@/components/Form/FormTextArea";
import { Menu01Icon } from "@/components/Icons/Menu01Icon";
import { Trash02Icon } from "@/components/Icons/Trash02Icon";
import { Button } from "@/design-system/Button";
import { FormField } from "@/design-system/FormField";
import { Info } from "@/design-system/Info";
import { Inline } from "@/design-system/Inline";
import { Stack } from "@/design-system/Stack";
import { createRequiredStringRule } from "@/helpers/rules";
import { useTranslation } from "@/translations";
import type { DragEndEvent, DragStartEvent } from "@/ui-adapters/DndKit";
import { DragOverlay, SortableTransform, useSortable } from "@/ui-adapters/DndKit";

import { PlusIcon } from "../Icons/PlusIcon";

interface Option {
  oId?: string;
  option: string;
}

export interface Question {
  qId?: string;
  type: QuestionDto["type"];
  question: string;
  options: Option[];
  useForAnalysis?: boolean;
}

interface FormWithQuestions extends FieldValues {
  questions: Question[];
}

type QuestionsArrayName<T extends FormWithQuestions> = Extract<ArrayPath<T>, "questions">;

type OptionsArrayName<T extends FormWithQuestions> = Extract<ArrayPath<T>, `questions.${number}.options`>;

interface Props<T extends FormWithQuestions> {
  idx: number;
  control: Control<T>;
  updateField: UseFieldArrayUpdate<T, QuestionsArrayName<T>>;
  types: readonly QuestionDto["type"][];
  question: FieldArrayWithId<T, QuestionsArrayName<T>, "id" | "type">;
  showOptions?: boolean;
  showUseForAnalysisOption?: boolean;
  isUseForAnalysisDisabled?: boolean;
}

const MAX_LENGTH_QUESTION = 255;

export function FormQuestion<T extends FormWithQuestions>({
  idx,
  control,
  updateField,
  types,
  question,
  showOptions,
  showUseForAnalysisOption,
  isUseForAnalysisDisabled,
}: Props<T>): React.ReactNode {
  const { t } = useTranslation();
  const [dragItemId, setDragItemId] = useState<number | null>(null);
  const { fields, append, remove, move } = useFieldArray<T, OptionsArrayName<T>>({
    control: control,
    name: `questions.${idx}.options` as OptionsArrayName<T>,
  });

  const form = useFormContext<T>();

  const { register, unregister, getValues } = form;
  useEffect(() => {
    const fieldName = `questions.${idx}.optionsValidation` as any;
    if (question.type === "multipleChoice") {
      register(fieldName, {
        validate: () => {
          const options = getValues(`questions.${idx}.options` as any) as Option[];
          const validOptions = options.filter((o: Option) => o.option && o.option.trim().length > 0);

          return validOptions.length >= 2 || t("page.surveys.create-or-edit.form.question.validation.minimum-options");
        },
      });
    } else {
      unregister(fieldName);
    }

    return () => unregister(fieldName);
  }, [question.type, idx, register, unregister, getValues, t]);

  function handleDragStart(event: DragStartEvent) {
    setDragItemId(event.active.data.current?.sortable.index);
  }

  function handleDragEnd(event: DragEndEvent) {
    setDragItemId(null);
    const { active, over } = event;

    if (over && active.id !== over.id) {
      if (active.data.current?.sortable && over.data.current?.sortable) {
        move(active.data.current.sortable.index, over.data.current.sortable.index);
      }
    }
  }

  return (
    <Stack gap="md" data-testid="form-question">
      <Form.Body>
        <FormField label={t("page.surveys.create-or-edit.form.question.text.label")} required>
          <FormTextArea<FormWithQuestions>
            data-testid="survey-question-textarea"
            name={`questions.${idx}.question`}
            rows={1}
            placeholder={t("page.surveys.create-or-edit.form.question.text.placeholder")}
            rules={{
              maxLength: {
                message: t("components.form.error.max-length", { length: MAX_LENGTH_QUESTION }),
                value: MAX_LENGTH_QUESTION,
              },
              validate: {
                required: createRequiredStringRule(t, "page.surveys.create-or-edit.form.question.text.label"),
              },
            }}
          />
        </FormField>
        <FormField label={t("page.surveys.create-or-edit.form.question.type.label")} required>
          <FormSelect<FormWithQuestions, QuestionDto["type"]>
            width="compact"
            name={`questions.${idx}.type`}
            items={types}
            defaultValue={question.type}
            keySelector={(x) => x}
            renderOption={(x) => {
              switch (x) {
                case "multipleChoice":
                  return t("page.surveys.create-or-edit.question.type.multiple-choice");
                case "yesNo":
                  return t("page.surveys.create-or-edit.question.type.yes-no");
                case "open":
                  return t("page.surveys.create-or-edit.question.type.open");
                case "rating":
                  return t("page.surveys.create-or-edit.question.type.rating");
              }
            }}
            onChange={(type) => {
              const currentQuestion = form.getValues(`questions.${idx}` as any);
              updateField(idx, { ...currentQuestion, type } as any);
              if (type === "multipleChoice" && !currentQuestion.options?.length) {
                append({ option: "" } as Option as any);
              }
            }}
            rules={{
              required: t("components.form.error.required", {
                inputName: t("page.surveys.create-or-edit.form.question.type.label"),
              }),
            }}
          />
        </FormField>
        {showOptions && (
          <div className="ml-space-md">
            <DynamicRepeater
              onDragStart={handleDragStart}
              onDragEnd={handleDragEnd}
              itemIds={fields.map((field) => field.id)}
            >
              <Stack gap="sm">
                {fields.map((option, optionIdx) => (
                  <FormOption<T>
                    key={option.id}
                    id={option.id}
                    questionIdx={idx}
                    optionIdx={optionIdx}
                    removeField={remove}
                    showDelete={fields.length > 1}
                  />
                ))}
              </Stack>
              <DragOverlay>
                {dragItemId !== null ? (
                  <article className="flex items-center gap-space-sm rounded-md border border-border-default bg-background-default px-field-inline py-field-block shadow-overlay">
                    <span className="shrink-0 text-text-secondary">
                      <Menu01Icon />
                    </span>
                    <span className="flex-1 text-body text-text-secondary">
                      {(fields[dragItemId] as unknown as Option)?.option ||
                        t("page.surveys.create-or-edit.form.question.option.placeholder")}
                    </span>
                  </article>
                ) : null}
              </DragOverlay>
            </DynamicRepeater>
          </div>
        )}
        {(question.type === "open" || question.type === "rating") && (
          <p className="ml-space-md text-caption text-text-secondary">
            {question.type === "open"
              ? t("page.surveys.create-or-edit.question.type.open.preview")
              : t("page.surveys.create-or-edit.question.type.rating.preview")}
          </p>
        )}
        {question.type === "yesNo" && (
          <ul className="ml-space-md flex flex-col gap-space-sm">
            {[t("common.yes"), t("common.no")].map((label) => (
              <li key={label} className="flex items-center gap-space-sm text-text-secondary">
                <span className="shrink-0 text-text-disabled">
                  <Menu01Icon />
                </span>
                <span className="flex-1 rounded-lg border border-border-secondary bg-background-secondary px-field-inline py-field-block text-body text-text-secondary">
                  {label}
                </span>
              </li>
            ))}
          </ul>
        )}
        {question.type === "rating" && showUseForAnalysisOption && (
          <Inline gap="sm" as="span">
            <FormCheckbox<FormWithQuestions>
              name={`questions.${idx}.useForAnalysis`}
              label={t("page.surveys.create-or-edit.form.question.use-for-analysis.label")}
              disabled={isUseForAnalysisDisabled}
            />
            <Info tooltip={t("page.surveys.create-or-edit.form.question.use-for-analysis.tooltip")} />
          </Inline>
        )}
        {question.type === "multipleChoice" && (
          <footer className="-mt-space-md ml-space-md">
            <FormErrorWrapper<FormWithQuestions> name={`questions.${idx}.optionsValidation`}>
              <Button
                data-testid="add-option"
                variant="ghostPrimary"
                icon={<PlusIcon />}
                onClick={() => {
                  append({ option: "" } as Option as any);
                }}
              >
                {t("page.surveys.create-or-edit.form.question.option.add")}
              </Button>
            </FormErrorWrapper>
          </footer>
        )}
      </Form.Body>
    </Stack>
  );
}

interface FormOptionProps {
  id: string;
  questionIdx: number;
  optionIdx: number;
  removeField: UseFieldArrayRemove;
  showDelete: boolean;
}

function FormOption<T extends FormWithQuestions>({
  id,
  questionIdx,
  optionIdx,
  removeField,
  showDelete,
}: FormOptionProps): React.ReactNode {
  const { t } = useTranslation();
  const form = useFormContext<T>();
  const options = useWatch({
    control: form.control as unknown as Control<FormWithQuestions>,
    name: `questions.${questionIdx}.options`,
  });

  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });

  return (
    <SortableTransform
      ref={setNodeRef}
      transform={transform}
      transition={transition}
      className={twJoin("flex items-start gap-space-sm", isDragging && "opacity-50")}
    >
      <span {...attributes} {...listeners} className="mt-space-sm shrink-0 cursor-grab text-text-secondary">
        <Menu01Icon />
      </span>
      <div className="flex-1">
        <FormInput<FormWithQuestions>
          data-testid="survey-form-option"
          name={`questions.${questionIdx}.options.${optionIdx}.option`}
          placeholder={t("page.surveys.create-or-edit.form.question.option.placeholder")}
          rules={{
            maxLength: {
              message: t("components.form.error.max-length", { length: MAX_LENGTH_QUESTION }),
              value: MAX_LENGTH_QUESTION,
            },
            validate: {
              required: createRequiredStringRule(t, "page.surveys.create-or-edit.form.question.type.label"),
              noDuplicates: (value: string) => {
                value = value?.trim();

                if (!value || !options) {
                  return;
                }

                const duplicateCount = options.filter(
                  (option, index) => index !== optionIdx && option.option?.trim().toLowerCase() === value,
                ).length;

                return (
                  duplicateCount === 0 || t("page.surveys.create-or-edit.form.question.option.validation.duplicate")
                );
              },
            },
          }}
        />
      </div>
      {showDelete && (
        <div className="mt-space-xs">
          <IconButton
            size="sm"
            variant="filled"
            tone="error"
            title={t("page.surveys.create-or-edit.form.question.option.delete")}
            onClick={() => removeField(optionIdx)}
            icon={<Trash02Icon />}
          />
        </div>
      )}
    </SortableTransform>
  );
}
